import { store, RootState } from 'index'
import { BASE_API_URL } from 'constants/env'
import { apiHeaders } from 'utils/api-headers'

export type BugReport = {
  title: string
  description: string
  priority: number | null
  reporter_email: string
  device_details: string
  other_device_details: string
  issue_type: string
  other_issue_type: string
  hubble_url: string
  loom_video_url: string
  file_url: string
}

export type BugReportResult = {
  issue: { id: string; identifier: string; url: string }
  warnings: string[]
}

export const BUG_REPORT_OUTCOME_UNKNOWN =
  'Your report may already exist in Linear. Check Linear before starting a new report to avoid creating a duplicate.'

export const bugReportSession = (state: RootState) => ({
  token: state.user.apiToken,
  userId: state.user.currentUser?.uuid || state.user.currentUser?.id,
  admin: state.user.currentUser?.is_admin === true,
})

export const sameBugReportSession = (
  first: ReturnType<typeof bugReportSession>,
  second: ReturnType<typeof bugReportSession>
) =>
  first.token === second.token &&
  first.userId === second.userId &&
  first.admin === second.admin

export class BugReportRequestError extends Error {
  outcomeUnknown: boolean

  constructor(message: string, outcomeUnknown = false) {
    super(message)
    this.name = 'BugReportRequestError'
    this.outcomeUnknown = outcomeUnknown
  }
}

const unknownResult = () =>
  new BugReportRequestError(BUG_REPORT_OUTCOME_UNKNOWN, true)

const safeIssueURL = (value: unknown): value is string => {
  if (typeof value !== 'string') return false
  try {
    const url = new URL(value)
    return (
      url.protocol === 'https:' &&
      url.hostname === 'linear.app' &&
      !url.port &&
      !url.username &&
      !url.password
    )
  } catch {
    return false
  }
}

export const submitBugReport = async (
  report: BugReport
): Promise<BugReportResult> => {
  const state = store.getState()
  const session = bugReportSession(state)
  if (!BASE_API_URL || !session.token || !session.userId || !session.admin) {
    throw new BugReportRequestError(
      'Sign in with an admin account to report a bug.'
    )
  }
  const assertSession = () => {
    if (!sameBugReportSession(session, bugReportSession(store.getState()))) {
      throw unknownResult()
    }
  }
  const controller = new AbortController()
  // The server's complete pipeline is bounded to 20 seconds. A browser timeout
  // or disconnect does not prove that issue creation failed; never retry here.
  const timeout = setTimeout(() => controller.abort(), 30000)
  try {
    const response = await fetch(
      `${BASE_API_URL.replace(/\/$/, '')}/admin/api/v4/bug_reports`,
      {
        method: 'POST',
        headers: apiHeaders(state),
        body: JSON.stringify(report),
        redirect: 'error',
        signal: controller.signal,
      }
    )
    assertSession()
    const json = await response.json().catch(() => null)
    assertSession()
    if (!response.ok) {
      const code = json?.error?.code
      if (
        code === 'outcome_unknown' ||
        (response.status >= 500 && code !== 'unavailable')
      ) {
        throw unknownResult()
      }
      const message =
        typeof json?.error?.message === 'string'
          ? json.error.message
          : response.status === 401 || response.status === 403
          ? 'Your account is not authorized to report a bug. Sign in again.'
          : 'The report was not accepted. Check the form and try again.'
      throw new BugReportRequestError(message)
    }
    if (
      typeof json?.issue?.id !== 'string' ||
      !json.issue.id ||
      typeof json?.issue?.identifier !== 'string' ||
      !json.issue.identifier ||
      !safeIssueURL(json?.issue?.url) ||
      !Array.isArray(json?.warnings) ||
      !json.warnings.every((warning: unknown) => typeof warning === 'string')
    ) {
      throw unknownResult()
    }
    return json as BugReportResult
  } catch (error) {
    if (error instanceof BugReportRequestError) throw error
    throw unknownResult()
  } finally {
    clearTimeout(timeout)
  }
}
